<template>
  <div>
    <div class="my-2">
      <label for="file">{{ label }}</label>
    </div>
    <div class="v-input">
      <div
        v-if="$slots.prepend"
        class="v-input__prepend-outer"
      >
        <slot name="prepend" />
      </div>
      <FilePond
        id="multi-files-upload"
        ref="multiUpload"
        name="file"
        class="v-input__control"
        :label-idle="idleQuote"
        :accepted-file-types="accept"
        :max-file-size="maxFileSize"
        :max-files="maxFiles"
        :server="server"
        :files="file"
        :instant-upload="instantUpload"
        :item-insert-location="itemInsertLocation"
        :file-validate-type-label-expected-types-map="typeWarnMsg"
        :style-item-panel-aspect-ratio="itemAspectRatio"
        :style="cssVars"
        :allow-browse="enableBrowse"
        :allow-remove="enableRemove"
        :allow-reorder="true"
        credits="false"
        :allow-multiple="maxFiles !== 1"
        drop-validation
        @processfile="fileUpload"
        @removefile="fileRemove"
        @activatefile="fileDownload"
        @reorderfiles="fileReorder"
        @updatefiles="emitOutput"
      />
    </div>
  </div>
</template>

<script>
/*
--- Required Pakage ---
npm install vue-filepond filepond --save
npm i filepond-plugin-image-preview --save
npm i filepond-plugin-file-validate-type --save
npm i filepond-plugin-file-validate-size --save
npm i filepond-plugin-file-poster --save
npm i autoprefixer@9.8.6

--- Required CSS ---
import 'filepond/dist/filepond.min.css'
import 'filepond-plugin-file-poster/dist/filepond-plugin-file-poster.css'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css'
*/

// 'upload-done' event will be emmited to indicates file upload progress
// <<false>> indicates file is added but is still or haven't uploaded
// <<true>> indicates that file has been uploaded

// Known Bug: Items are undragable from third row onwards

import vueFilePond, { setOptions } from 'vue-filepond'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'
import FilePondPluginFilePoster from 'filepond-plugin-file-poster'
// import zhCn from 'filepond/locale/zh-cn.js'

const FilePond = vueFilePond(FilePondPluginImagePreview, FilePondPluginFileValidateType, FilePondPluginFileValidateSize, FilePondPluginFilePoster)

export default {
  components: {
    FilePond
  },
  props: {
    // v-model value
    value: {
      type: [Array, null],
      default: () => { return [] }
    },
    // sets input label
    label: {
      type: String,
      default: null
    },
    // accepted file types, seperated by comma
    accept: {
      required: false,
      type: String,
      default: null
    },
    // maximum file size allowable to upload, should be consistence with server settings
    maxFileSize: {
      type: String,
      default: '100MB'
    },
    // maximum file number allowable to upload
    maxFiles: {
      type: [Number, String],
      default: null,
      validator: val => !isNaN(val)
    },
    // allow mock files, images won't be downloaded and no preview is shown
    mock: {
      type: Boolean,
      default: false
    },
    // allow thumbnails for picture preview
    thumbnail: {
      type: Boolean,
      default: false
    },
    // enable instant upload of files
    instantUpload: {
      type: Boolean,
      default: true
    },
    // new uploads item insert location, available options: 'after', 'before'
    itemInsertLocation: {
      type: String,
      default: 'after'
    },
    // the aspect ratio for displaying items, set to null for dynamic display
    itemAspectRatio: {
      type: String,
      default: '0.56'
    },
    // the number of columns in each row
    columnNums: {
      type: Number,
      default: 4
    },
    // the max height for filepond element
    maxHeight: {
      type: String,
      default: ''
    },
    // file prompt messsage for users
    labelIdle: {
      type: String,
      default: null
    },
    // specifies key for the file uploaded
    fileKey: {
      type: String,
      default: null
    },
    // url for normal download api
    fullDownloadUrl: {
      type: String,
      default: 'download.api?path='
    },
    // url for thumbnail download api
    thumbDownloadUrl: {
      type: String,
      default: 'download.api?thumb=true&path='
    },
    enableRemove: {
      type: Boolean,
      default: true
    },
    enableBrowse: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      // Thumbnail placeholders, replace when needed
      image_poster: require('@/assets/images/multifile_upload/image_poster.png'),
      audio_poster: require('@/assets/images/multifile_upload/audio_poster.png'),
      video_poster: require('@/assets/images/multifile_upload/video_poster.png'),
      document_poster: require('@/assets/images/multifile_upload/document_poster.png'),
      /// -------------------------------------------------------- ///
      resBuffer: [],
      server: {
        url: window.API_URL,
        process: {
          url: 'upload.api',
          method: 'POST',
          onload: (response) => {
            this.fileResponse(response)
          }
        },
        load: {
          url: (this.thumbnail ? this.thumbDownloadUrl : this.fullDownloadUrl),
          method: 'GET'
        },
        revert: null,
        restore: null
      },
      file: [],
      idleQuote: '<span class="filepond--label-action"> Click Here </span> or Drag & Drop your files',
      typeWarnMsg: {}
    }
  },
  computed: {
    cssVars () {
      if (this.maxFiles === 1) return null
      return {
        '--item-width': 100 / this.columnNums + '%',
        '--max-height': this.maxHeight
      }
    }
  },
  watch: {
    value (val) {
      this.init()
    }
  },
  async mounted () {
    // console.log('AtFilePicker mounted')
    const filePondLocale = await import(/* webpackChunkName: "vuefilepond-[request]" */ 'filepond/locale/' + this.convertLocale(this.currentLocale, 'vuefilepond').toLowerCase() + '.js')
    setOptions(filePondLocale.default)
    this.typeWarnMsg = { 'image/*': this.$t('components.AtFilePicker.image'), 'video/*': this.$t('components.AtFilePicker.video'), 'audio/*': this.$t('components.AtFilePicker.audio'), 'application/*': this.$t('components.AtFilePicker.application') }
    if (this.labelIdle != null) {
      this.idleQuote = this.labelIdle
    }
  },
  methods: {
    init () {
      const f = []
      let allServerFiles = true
      this.value.forEach((item, index) => {
        if (!item.id) { allServerFiles = false }
        f.push({
          source: item.path,
          options: {
            type: 'local',
            metadata: item
          }
        })
        // Sets mock file attributes & display mock file
        if (this.mock || !item.mimetype.startsWith('image')) {
          f[index].options.file = {
            name: item.name,
            size: item.size,
            type: item.mimetype
          }
          // Sets posters
          if (item.mimetype.startsWith('image') && this.mock) {
            f[index].options.metadata.poster = this.image_poster
          } else if (item.mimetype.startsWith('audio')) {
            f[index].options.metadata.poster = this.audio_poster
          } else if (item.mimetype.startsWith('video')) {
            f[index].options.metadata.poster = this.video_poster
          } else {
            f[index].options.metadata.poster = this.document_poster
          }
        }
      })
      if (allServerFiles) {
        this.file = f
      }
    },
    fileResponse (response) {
      this.resBuffer.push(JSON.parse(response))
    },
    fileUpload (err, file) {
      if (err == null) {
        const metadata = this.resBuffer.shift()
        const keys = Object.keys(metadata)

        // eslint-disable-next-line
        for (const key of keys) {
          file.setMetadata(key, metadata[key], true)
        }
        file.setMetadata('key', this.fileKey, true)

        this.emitOutput()
      }
    },
    fileRemove () {
      this.emitOutput()
    },
    fileReorder () {
      this.emitOutput()
    },
    existsKey (arr, key) {
      if (arr) {
        return (key in arr)
      } else {
        return false
      }
    },
    fileDownload (file) {
      let url = window.API_URL + 'download.api?'
      if (this.existsKey(file.getMetadata(), 'path')) {
        url = url + 'name=' + file.getMetadata('name') + '&path=' + file.getMetadata('path')
      } else {
        url = url + 'name=' + file.getMetadata('name') + '&tmp=' + file.getMetadata('tmp')
      }
      const a = $('<a>')
        .attr('href', url)
        .attr('target', 'download="' + url + '"')
        .appendTo('body')
      a[0].click()
      a.remove()
    },
    emitOutput () {
      const result = []
      let flagReady = true
      const files = this.$refs.multiUpload.getFiles()

      files.forEach((file, index) => {
        const meta = file.getMetadata()

        if (file.status === 9 || file.status === 3) { flagReady = false }

        delete meta.color
        delete meta.poster
        meta.order = index

        result.push(meta)
      })

      if (flagReady) {
        this.$emit('upload-done', true)
        this.$emit('input', result)
      } else {
        this.$emit('upload-done', false)
      }
    }
  }
}
</script>

<style>
@media (min-width: 767px) {
  #multi-files-upload .filepond--item {
    width: calc(var(--item-width) - .5em);
  }
}

#multi-files-upload.filepond--root {
  max-height: var(--max-height);
}
/*
#multi-files-upload .filepond--file-wrapper > .filepond--file > .filepond--media-preview-wrapper {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
}

#multi-files-upload .filepond--file-wrapper > .filepond--file > .filepond--media-preview-wrapper {
  width: 100% !important;
  height: 100% !important;
}

#multi-files-upload .filepond--file-wrapper > .filepond--file > .filepond--media-preview-wrapper > .filepond--media-preview {
  width: 100% !important;
  height: 100% !important;
  display: flex;
  justify-content: center;
  align-items: center;
}

#multi-files-upload .filepond--file-wrapper > .filepond--file > .filepond--media-preview-wrapper > .filepond--media-preview > video {
  width: 100% !important;
  height: 100% !important;
}

[data-filepond-item-state=processing-complete] .filepond--item-panel {
    background-color: #263e46;
}
*/
</style>
